import React, { useEffect, useState, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useAppRouter } from '../../hooks/useAppRouter';
import { fetchReports } from '../../services/api';
import CollapsiblePanel from '../shared/CollapsiblePanel';
import { useUserGroups } from '../../hooks/useUserGroups';
import { FaChevronRight } from 'react-icons/fa';

interface RootState {
  sidebar: {
    selectedDept: string | null;
    selectedSubdept: string | null;
  };
  reports: {
    reports: any[];
    loadingReports: boolean;
    reportsError: string | null;
    reportSearch: string;
    reportsCollapsed: boolean;
  };
  tabs: {
    openTabs: string[];
    activeTab: string | null;
    tabReportInfo: Record<string, any>;
  };
}

const ReportList: React.FC = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { updateSearchParams } = useAppRouter();
  
  const selectedDept = useSelector((state: RootState) => state.sidebar.selectedDept);
  const selectedSubdept = useSelector((state: RootState) => state.sidebar.selectedSubdept);
  const reports = useSelector((state: RootState) => state.reports.reports);
  const loadingReports = useSelector((state: RootState) => state.reports.loadingReports);
  const reportsError = useSelector((state: RootState) => state.reports.reportsError);
  const reportSearch = useSelector((state: RootState) => state.reports.reportSearch);
  const reportsCollapsed = useSelector((state: RootState) => state.reports.reportsCollapsed);
  const openTabs = useSelector((state: RootState) => state.tabs.openTabs);
  const activeTab = useSelector((state: RootState) => state.tabs.activeTab);
  const tabReportInfo = useSelector((state: RootState) => state.tabs.tabReportInfo);
  const [closingTabId, setClosingTabId] = useState<string | null>(null);
  const { groups: userGroups, loading: groupsLoading } = useUserGroups();
  const [isMobile, setIsMobile] = useState(false);
  const [mobileModalOpen, setMobileModalOpen] = useState(false);
  const lastFetchedReports = useRef<string>('');

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 600);
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fetch reports when department or subdepartment changes
  useEffect(() => {
    // Wait for groups to finish loading before making the request
    if (groupsLoading) return;
    
    if (!selectedDept || !selectedSubdept) {
      // Do not clear reports, just skip fetching
      dispatch({ type: 'reports/setLoading', payload: false });
      dispatch({ type: 'reports/setError', payload: null });
      lastFetchedReports.current = '';
      return;
    }
    
    // Create a key that includes department, subdepartment, and groups
    const fetchKey = `${selectedDept}:${selectedSubdept}:${JSON.stringify([...userGroups].sort())}`;
    
    // Only fetch if the parameters have actually changed
    if (lastFetchedReports.current === fetchKey) {
      return;
    }
    
    lastFetchedReports.current = fetchKey;
    
    dispatch({ type: 'reports/setLoading', payload: true });
    dispatch({ type: 'reports/setError', payload: null });
    
    fetchReports(selectedDept, selectedSubdept, userGroups)
      .then(data => {
        dispatch({ type: 'reports/setReports', payload: Array.isArray(data) ? data.sort((a: any, b: any) => a.displayname.localeCompare(b.displayname)) : [] });
        dispatch({ type: 'reports/setLoading', payload: false });
      })
      .catch(err => {
        dispatch({ type: 'reports/setError', payload: 'Could not load reports' });
        dispatch({ type: 'reports/setLoading', payload: false });
      });
  }, [selectedDept, selectedSubdept, dispatch, userGroups, groupsLoading]);

  const handleReportClick = (reportId: string) => {
    const report = reports.find(r => r.reportid === reportId);
    if (!report) return;

    const tabId = `${report.workspaceid}-${report.reportid}`;
    const isOpen = openTabs.includes(tabId);

    if (isOpen) {
      if (activeTab === tabId) {
        updateSearchParams({ workspaceId: '', reportId: '' });
        setTimeout(() => {
          dispatch({ type: 'tabs/removeTab', payload: tabId });
          dispatch({ type: 'tabs/setActiveTab', payload: null });
        }, 0);
      } else {
        dispatch({ type: 'tabs/setActiveTab', payload: tabId });
        updateSearchParams({ workspaceId: report.workspaceid, reportId: report.reportid });
      }
      return;
    }

    dispatch({ type: 'tabs/addTab', payload: tabId });
    dispatch({ type: 'tabs/setTabReportInfo', payload: { ...tabReportInfo, [tabId]: report } });
    dispatch({ type: 'tabs/setActiveTab', payload: tabId });
    updateSearchParams({ workspaceId: report.workspaceid, reportId: report.reportid });
  };

  // Clear closingTabId when openTabs changes
  useEffect(() => {
    setClosingTabId(null);
  }, [openTabs]);

  const handleToggleCollapse = () => {
    dispatch({ type: 'reports/toggleCollapsed' });
  };

  const filteredReports = reports.filter((report: any) =>
    report.displayname.toLowerCase().includes(reportSearch.toLowerCase()) ||
    report.report.toLowerCase().includes(reportSearch.toLowerCase())
  );

  // Deduplicate reports by workspaceid and reportid
  const uniqueReports = Array.from(
    new Map(filteredReports.map(r => [`${r.workspaceid}-${r.reportid}`, r])).values()
  );

  const normalize = (s: string): string => s.trim().toLowerCase();
  const userGroupSet = new Set<string>((userGroups ?? []).map(normalize));

  // Filter reports by Azure Security Group Name
  const visibleReports = uniqueReports.filter(report => {
  const raw = report.azureSecurityGroupName;

  // No restriction => visible to all
  if (!raw) return true;

  // Split on ';', trim, drop empties
  const reportGroups: string[] = raw
    .split(';')
    .map(normalize)
    .filter((g: string): g is string => g.length > 0);

    // If nothing valid after splitting, treat as unrestricted
    if (reportGroups.length === 0) return true;

    // Visible if the user is in ANY of the report's groups
    return reportGroups.some(name => userGroupSet.has(name));
  });

  if (groupsLoading) {
    return <div className="report-list-loading">{t('common.loading')}</div>;
  }

  if (isMobile) {
    let selectedReportName = '';
    if (activeTab && tabReportInfo[activeTab]) {
      selectedReportName = tabReportInfo[activeTab].displayname || '';
    }
    return (
      <>
        <div className="report-list-mobile-bar">
          <span className="report-list-mobile-title">{t('reports.reports')}</span>
          <button className="report-list-mobile-menu" onClick={() => setMobileModalOpen(true)} aria-label="Open Reports Menu">
            {FaChevronRight({ size: 18 })}
          </button>
          {selectedReportName && (
            <span className="selected-dept-label">{selectedReportName}</span>
          )}
        </div>
        {mobileModalOpen && (
          <div className="sidebar-modal-overlay" onClick={() => setMobileModalOpen(false)}>
            <div className="sidebar-modal" onClick={e => e.stopPropagation()}>
              <button className="sidebar-modal-close" onClick={() => setMobileModalOpen(false)}>&times;</button>
              <div style={{ padding: 16 }}>
                <div style={{ fontWeight: 600, fontSize: '1.1rem', marginBottom: 12 }}>{t('reports.reports')}</div>
                <input
                  className="content-search-input"
                  type="text"
                  placeholder={t('common.search')}
                  value={reportSearch}
                  onChange={e => dispatch({ type: 'reports/setSearch', payload: e.target.value })}
                  style={{ marginBottom: 12, width: '100%' }}
                />
                <div className="report-list">
                  {loadingReports ? (
                    <div className="report-list-loading">{t('common.loading')}</div>
                  ) : !reportsError && visibleReports.length > 0 ? (
                    visibleReports.map((report) => {
                      const tabId = `${report.workspaceid}-${report.reportid}`;
                      const isOpen = openTabs.includes(tabId) && closingTabId !== tabId;
                      const isActive = activeTab === tabId;
                      return (
                        <div
                          className={`report-card${isOpen ? ' open' : ''}${isActive ? ' active' : ''}`}
                          key={`${report.workspaceid}-${report.reportid}`}
                          onClick={() => { handleReportClick(report.reportid); setMobileModalOpen(false); }}
                          title={report.displayname}
                        >
                          <div className="report-title">{report.displayname}</div>
                          <div className="report-desc">{report.report}</div>
                          {isOpen && <div className="report-status">{t('reports.open')}</div>}
                        </div>
                      );
                    })
                  ) : (
                    <div className="report-list-empty">
                      {selectedDept ? t('reports.noReportsFound') : t('reports.selectDepartmentToView')}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </>
    );
  }

  return (
    <CollapsiblePanel
      title={t('reports.reports')}
      collapsed={reportsCollapsed}
      onToggleCollapse={handleToggleCollapse}
      panelClassName={`report-list-wrapper${reportsCollapsed ? ' collapsed' : ''}`}
    >
      {!reportsCollapsed ? (
        <div className="report-list">
          {loadingReports ? (
            <div className="report-list-loading">{t('common.loading')}</div>
          ) : !reportsError && visibleReports.length > 0 ? (
            visibleReports.map((report) => {
              const tabId = `${report.workspaceid}-${report.reportid}`;
              const isOpen = openTabs.includes(tabId) && closingTabId !== tabId;
              const isActive = activeTab === tabId;
              return (
                <div
                  className={`report-card${isOpen ? ' open' : ''}${isActive ? ' active' : ''}`}
                  key={`${report.workspaceid}-${report.reportid}`}
                  onClick={() => handleReportClick(report.reportid)}
                  title={report.displayname}
                >
                  <div className="report-title">{report.displayname}</div>
                  <div className="report-desc">{report.report}</div>
                  {isOpen && <div className="report-status">Open</div>}
                </div>
              );
            })
          ) : (
            <div className="report-list-empty">
              {selectedDept ? t('reports.noReportsFound') : t('reports.selectDepartmentToView')}
            </div>
          )}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', margin: '8px 0 16px 0' }}>
          <span className="report-list-collapsed-label">{t('reports.reports')}</span>
          <button
            className="report-list-toggle report-list-toggle-collapsed"
            onClick={handleToggleCollapse}
          >
            {'>'}
          </button>
        </div>
      )}
    </CollapsiblePanel>
  );
};

export default ReportList; 